import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Swal from 'sweetalert2';
import './TravelInquiryForm.css';
import { Container } from 'react-bootstrap';

import { CheckImg } from '../components/SvgIcons';
import planeIcon from '../assets/image/plane.svg';
import { usePromoEnquiryMutation } from '../hooks/usePromoEnquiryMutation';
import { isEnabled } from '../lib/promoFlags';

const TravelInquiryForm = ({
  promoData = null,
  alias = '',
  promoSubdomain = '',
  subdomain = '',
  selectedTour = '',
  callbackEnabled = false,
  contactEnabled = true,
}) => {
  const [tourSearchOpen, setTourSearchOpen] = useState(false);
  const lastAppliedSelectedTourRef = useRef('');
  const isSubmittingRef = useRef(false);
  const enquiryMutation = usePromoEnquiryMutation(alias);

  const allTours = useMemo(() => {
    const items = [
      promoData?.tour_mapping?.main_tour,
      ...(promoData?.tour_mapping?.similar_tours || []),
      ...(promoData?.other_tours?.items || []),
    ].filter(Boolean);

    const unique = [];
    const seen = new Set();
    items.forEach((item) => {
      if (!seen.has(item.name)) {
        seen.add(item.name);
        unique.push(item.name);
      }
    });

    return unique;
  }, [promoData]);

  const tourOptions = useMemo(
    () => allTours.length ? allTours : [
      'Leh Ladakh Kargil Trip',
      'Nubra Valley Adventure',
      'Pangong Lake Retreat',
    ],
    [allTours]
  );

  const validationSchema = useMemo(
    () =>
      Yup.object({
        name: Yup.string().trim().min(2, 'Enter at least 2 characters').required('Name is required'),
        mobile: Yup.string()
          .matches(/^[0-9]{10}$/, 'Enter a valid 10-digit mobile number')
          .required('Mobile number is required'),
        email: Yup.string().email('Enter a valid email address').required('Email is required'),
        singleTourSearch: Yup.string(),
        placeToVisit: Yup.string().trim(),
        noOfTravellers: Yup.number()
          .transform((value, originalValue) => (originalValue === '' ? undefined : value))
          .typeError('Enter number of travellers')
          .integer('Only whole numbers are allowed')
          .min(1, 'At least 1 traveller is required')
          .nullable(),
        noOfChildren: Yup.string(),
      }),
    []
  );

  const formik = useFormik({
    initialValues: {
      name: '',
      mobile: '',
      email: '',
      singleTourSearch: '',
      placeToVisit: '',
      noOfTravellers: '',
      noOfChildren: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      if (isSubmittingRef.current || enquiryMutation.isPending) {
        setSubmitting(false);
        return;
      }

      isSubmittingRef.current = true;
      const hasPromoIdentifier = Boolean(alias || promoSubdomain);

      if (!subdomain || !hasPromoIdentifier) {
        enquiryMutation.reset();
        await Swal.fire({
          icon: 'error',
          title: 'Unable to submit',
          text: 'Missing promo mapping details for this enquiry.',
          confirmButtonColor: '#17bfc6',
        });
        setSubmitting(false);
        isSubmittingRef.current = false;
        return;
      }

      try {
        await enquiryMutation.mutateAsync({
          ...values,
          alias,
          subdomain,
          promo_subdomain: promoSubdomain,
          page_url: window.location.href,
        });
        resetForm();
        enquiryMutation.reset();
        await Swal.fire({
          icon: 'success',
          title: 'Enquiry submitted',
          text: 'Your enquiry has been submitted successfully.',
          confirmButtonColor: '#17bfc6',
        });
      } catch (error) {
        await Swal.fire({
          icon: 'error',
          title: 'Submission failed',
          text: error?.message || 'Unable to submit enquiry.',
          confirmButtonColor: '#17bfc6',
        });
      } finally {
        isSubmittingRef.current = false;
        setSubmitting(false);
      }
    },
  });
  const setFormikFieldValue = formik.setFieldValue;
  const canShowCallback = isEnabled(callbackEnabled, false);
  const canShowContact = isEnabled(contactEnabled, true);

  useEffect(() => {
    const normalizedSelectedTour = String(selectedTour || '').trim();

    if (!normalizedSelectedTour || lastAppliedSelectedTourRef.current === normalizedSelectedTour) {
      return;
    }

    setFormikFieldValue('singleTourSearch', normalizedSelectedTour, false);
    lastAppliedSelectedTourRef.current = normalizedSelectedTour;
    setTourSearchOpen(false);
  }, [selectedTour, setFormikFieldValue]);

  const hasError = (fieldName) => formik.touched[fieldName] && formik.errors[fieldName];
  const filteredTours = useMemo(() => {
    const query = formik.values.singleTourSearch.trim().toLowerCase();
    if (!query) return tourOptions;
    return tourOptions.filter((tour) => tour.toLowerCase().includes(query));
  }, [formik.values.singleTourSearch, tourOptions]);

  const handleTourSelect = (tour) => {
    formik.setFieldValue('singleTourSearch', tour);
    setTourSearchOpen(false);
  };

  return (
    <div className="travel-inquiry-container">
      <Container>
        <div className="row">
          <div className="col-lg-5 d-flex align-items-center justify-content-center inquiry-left">
            <div className="promotional-content text-white text-center text-lg-start">
              <h1 className="display-4 fw-bold mb-3">
                {promoData?.enquiry?.title || 'Customize your'}
              </h1>

              {promoData?.enquiry?.description ? (
                <p className="mb-4">{promoData.enquiry.description}</p>
              ) : null}

              <div className="contact-details">
                <div className="contact-item d-flex align-items-center mb-3">
                  <div className="checkmark-icon me-2">
                    <CheckImg />
                  </div>
                  <span className="checkmark-heading">{promoData?.enquiry?.booking_label || 'Get Best Deals'}</span>
                </div>

                {canShowCallback ? (
                  <div className="contact-item d-flex align-items-center mb-3">
                    <div className="checkmark-icon me-2">
                      <CheckImg />
                    </div>
                    <span className="checkmark-heading">Call us Now : <span> {promoData?.enquiry?.callback_mobile || '+91 9816348636'} </span></span>
                  </div>
                ) : null}

                {canShowCallback ? (
                  <div className="contact-item d-flex align-items-center mb-3">
                    <div className="checkmark-icon me-2">
                      <CheckImg />
                    </div>
                    <span className="checkmark-heading">Whatsapp Number : <span> {promoData?.enquiry?.whatsapp_number || '+91 8826303636'} </span></span>
                  </div>
                ) : null}

                {canShowContact ? (
                  <div className="contact-item d-flex align-items-center mb-3">
                    <div className="checkmark-icon me-2">
                      <CheckImg />
                    </div>
                    <span className="checkmark-heading">Mail us Now : <span> {promoData?.contact?.email_primary || 'help@bharatbooking.com'} </span></span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          <div className="col-lg-7 d-flex align-items-center justify-content-center">
            <div className="form-container w-100">
              <div className="banner-content-bottom d-flex justify-content-center justify-content-lg-start plane-btn inquiry-cta">
                <a href="#similar-tours" className="primary-btn4">
                  <span>
                    {promoData?.enquiry?.call_me_label || 'View All Package'}
                    <img src={planeIcon} alt="plane" className="icon ms-2" />
                  </span>
                </a>
              </div>
              <div className="form-card">
                <h2 className="form-title mb-4 dark-black fw-bolder">{promoData?.enquiry?.title || 'Where Do You Want To Go ?'}</h2>

                <form onSubmit={formik.handleSubmit} noValidate>
                  <div className="row">
                    <div className="col-12">
                      <div className="mb-3">
                        <label htmlFor="singleTourSearch" className="form-label fw-semibold darkgray-text">Single Tour Search</label>
                        <div className="tour-search-box">
                          <input
                            type="text"
                            className={`form-control ${hasError('singleTourSearch') ? 'is-invalid' : ''}`}
                            id="singleTourSearch"
                            name="singleTourSearch"
                            placeholder="Search a tour package"
                            autoComplete="off"
                            value={formik.values.singleTourSearch}
                            onChange={formik.handleChange}
                            onFocus={() => setTourSearchOpen(true)}
                            onBlur={() => {
                              formik.handleBlur({
                                target: {
                                  name: 'singleTourSearch',
                                },
                              });
                              setTimeout(() => setTourSearchOpen(false), 150);
                            }}
                          />
                          {tourSearchOpen && filteredTours.length > 0 && (
                            <div className="tour-search-results">
                              {filteredTours.map((tour) => (
                                <button
                                  key={tour}
                                  type="button"
                                  className="tour-search-item"
                                  onMouseDown={() => handleTourSelect(tour)}
                                >
                                  {tour}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {hasError('singleTourSearch') && <div className="field-error">{formik.errors.singleTourSearch}</div>}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="name" className="form-label fw-semibold darkgray-text">Name*</label>
                        <input
                          type="text"
                          className={`form-control ${hasError('name') ? 'is-invalid' : ''}`}
                          id="name"
                          name="name"
                          placeholder="Enter your name"
                          value={formik.values.name}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {hasError('name') && <div className="field-error">{formik.errors.name}</div>}
                      </div>

                      <div className="mb-3">
                        <label htmlFor="mobile" className="form-label fw-semibold darkgray-text">Mobile No*</label>
                        <input
                          type="tel"
                          className={`form-control ${hasError('mobile') ? 'is-invalid' : ''}`}
                          id="mobile"
                          name="mobile"
                          placeholder="Enter Mobile No"
                          value={formik.values.mobile}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {hasError('mobile') && <div className="field-error">{formik.errors.mobile}</div>}
                      </div>

                      <div className="mb-3">
                        <label htmlFor="noOfTravellers" className="form-label fw-semibold darkgray-text">No of Travellers*</label>
                        <input
                          type="number"
                          className={`form-control ${hasError('noOfTravellers') ? 'is-invalid' : ''}`}
                          id="noOfTravellers"
                          name="noOfTravellers"
                          placeholder="Enter number of travellers"
                          value={formik.values.noOfTravellers}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {hasError('noOfTravellers') && <div className="field-error">{formik.errors.noOfTravellers}</div>}
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label fw-semibold darkgray-text">Email*</label>
                        <input
                          type="email"
                          className={`form-control ${hasError('email') ? 'is-invalid' : ''}`}
                          id="email"
                          name="email"
                          placeholder="Enter your Email"
                          value={formik.values.email}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {hasError('email') && <div className="field-error">{formik.errors.email}</div>}
                      </div>

                      <div className="mb-3">
                        <label htmlFor="placeToVisit" className="form-label fw-semibold darkgray-text">Place to visit query</label>
                        <input
                          type="text"
                          className={`form-control ${hasError('placeToVisit') ? 'is-invalid' : ''}`}
                          id="placeToVisit"
                          name="placeToVisit"
                          placeholder="Enter place to visit"
                          value={formik.values.placeToVisit}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        />
                        {hasError('placeToVisit') && <div className="field-error">{formik.errors.placeToVisit}</div>}
                      </div>

                      <div className="mb-3">
                        <label htmlFor="noOfChildren" className="form-label fw-semibold darkgray-text">No of Child</label>
                        <select
                          className={`form-select ${hasError('noOfChildren') ? 'is-invalid' : ''}`}
                          id="noOfChildren"
                          name="noOfChildren"
                          value={formik.values.noOfChildren}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                        >
                          <option value="">No of Child</option>
                          <option value="0">0</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                          <option value="5+">5+</option>
                        </select>
                        {hasError('noOfChildren') && <div className="field-error">{formik.errors.noOfChildren}</div>}
                      </div>
                    </div>
                  </div>

                  <div className="text-center mt-3">
                    <button
                      type="submit"
                      className="btn btn-primary submit-btn"
                      disabled={formik.isSubmitting || enquiryMutation.isPending}
                    >
                      {promoData?.enquiry?.submit_label || 'Submit'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default TravelInquiryForm;
